import { ButtonStyles, EmptyState, Icons, ListItem, MenuButton } from '@silverfort/sf-components';
import { SCOPE_SHOW_SERVICES, SCOPES_COLOR, SCOPES_TITLE } from './TierConsts';
import { TIER_PART_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import { useEffect, useState } from 'react';
import NameConventionDrawer from './NameConvention/NameConventionDrawer';
import { ConditionRow, GroupRow, IPRow, OURow, ServerRow } from '../../../../../../sharedComponents/scope/ResourcesRows';
import ResourcesDrawer from './Resources/ResourcesDrawer';

const TierPart = ({
    tierPartConfig,
    updateTierPart,
    scope,
    setOpenDrawerScope,
    openDrawerScope,
    emptyStateDescription
}) => {
    const [isResourcesDrawerOpen, setIsResourcesDrawerOpen] = useState(false);
    const [resourceToEdit, setResourceEdit] = useState();
    const [isNameConventionDrawerOpen, setIsNameConventionDrawerOpen] = useState(false);
    const [conditionToEdit, setConditionToEdit] = useState();

    useEffect(() => {
        if (scope !== openDrawerScope) {
            setIsNameConventionDrawerOpen(false);
            setIsResourcesDrawerOpen(false);
        }
    }, [openDrawerScope, scope]);

    const openResourcesDrawer = () => {
        setOpenDrawerScope(scope);
        setIsResourcesDrawerOpen(true);
        setIsNameConventionDrawerOpen(false);
    };

    const openNameConventionDrawer = () => {
        setOpenDrawerScope(scope);
        setIsNameConventionDrawerOpen(true);
        setIsResourcesDrawerOpen(false);
    };

    const onDeleteRow = (key, type) => {
        const typeConfig = { ...tierPartConfig[type] };
        delete typeConfig[key];
        updateTierPart(scope, { ...tierPartConfig, [type]: typeConfig });
    };
    const tierPartRows = [];

    for (const guid in tierPartConfig[TIER_PART_TYPES.GROUPS]) {
        const groupData = tierPartConfig[TIER_PART_TYPES.GROUPS][guid];
        tierPartRows.push(
            <GroupRow
                key={`group_${guid}`}
                guid={guid}
                name={groupData.name}
                domainName={groupData.domainName}
                services={groupData.services}
                protocols={groupData.protocols ?? []}
                onDelete={() => onDeleteRow(guid, TIER_PART_TYPES.GROUPS)}
                showProtocols={SCOPE_SHOW_SERVICES[scope]}
                onEdit={() => {
                    setResourceEdit({ id: guid, type: TIER_PART_TYPES.GROUPS });
                    openResourcesDrawer();
                }}
            />
        );
    }

    for (const guid in tierPartConfig[TIER_PART_TYPES.OUS]) {
        const ouData = tierPartConfig[TIER_PART_TYPES.OUS][guid];
        tierPartRows.push(
            <OURow
                key={`ou_${guid}`}
                guid={guid}
                name={ouData.name}
                domainName={ouData.domainName}
                services={ouData.services}
                protocols={ouData.protocols ?? []}
                onDelete={() => onDeleteRow(guid, TIER_PART_TYPES.OUS)}
                showProtocols={SCOPE_SHOW_SERVICES[scope]}
                onEdit={() => {
                    setResourceEdit({ id: guid, type: TIER_PART_TYPES.OUS });
                    openResourcesDrawer();
                }}
            />
        );
    }

    for (const guid in tierPartConfig[TIER_PART_TYPES.SERVERS]) {
        const serverData = tierPartConfig[TIER_PART_TYPES.SERVERS][guid];
        tierPartRows.push(
            <ServerRow
                key={`server_${guid}`}
                guid={guid}
                name={serverData.name}
                domainName={serverData.domainName}
                services={serverData.services}
                protocols={serverData.protocols ?? []}
                onDelete={() => onDeleteRow(guid, TIER_PART_TYPES.SERVERS)}
                showProtocols={SCOPE_SHOW_SERVICES[scope]}
                onEdit={() => {
                    setResourceEdit({ id: guid, type: TIER_PART_TYPES.SERVERS });
                    openResourcesDrawer();
                }}
            />
        );
    }

    for (const ip in tierPartConfig[TIER_PART_TYPES.IPS]) {
        const ipData = tierPartConfig[TIER_PART_TYPES.IPS][ip];
        const services = ipData.services ?? ipData;
        const protocols = ipData.protocols ?? [];
        if (!services && !protocols.length) {
            continue;
        }
        tierPartRows.push(
            <IPRow
                key={`ip_${ip}`}
                ip={ip}
                services={services ?? []}
                protocols={protocols}
                onDelete={() => onDeleteRow(ip, TIER_PART_TYPES.IPS)}
                showProtocols={SCOPE_SHOW_SERVICES[scope]}
                onEdit={() => {
                    setResourceEdit({ id: ip, type: TIER_PART_TYPES.IPS });
                    openResourcesDrawer();
                }}
            />
        );
    }

    const onUpdateConditions = (conditions) => {
        updateTierPart(scope, { ...tierPartConfig, [TIER_PART_TYPES.CONDITIONS]: conditions });
    };

    const onDeleteCondition = (index) => {
        const conditionsConfig = [...tierPartConfig[TIER_PART_TYPES.CONDITIONS]];
        conditionsConfig.splice(index, 1);
        updateTierPart(scope, { ...tierPartConfig, [TIER_PART_TYPES.CONDITIONS]: conditionsConfig });
    };
    tierPartConfig[TIER_PART_TYPES.CONDITIONS].forEach((condition, index) => {
        tierPartRows.push(
            <ConditionRow
                key={`condition_${index}`}
                condition={condition}
                onDelete={() => onDeleteCondition(index)}
                onEdit={() => {
                    setConditionToEdit(index);
                    openNameConventionDrawer();
                }}
                showProtocols={SCOPE_SHOW_SERVICES[scope]}
            />
        );
    });

    return (
        <div className="w-full border border-sf-gray-100 rounded-lg">
            <div className="flex justify-between items-center h-12 border-b border-sf-gray-100 px-4">
                <div className="flex text-sf items-center gap-2">
                    <div className={`h-2 w-2  rounded-full ${SCOPES_COLOR[scope]}`}></div>
                    <div>{`${SCOPES_TITLE[scope]} (${tierPartRows.length})`}</div>
                </div>
                <div>
                    <MenuButton
                        text={'Add'}
                        icon={Icons.Add}
                        className="right-0"
                        iconClassName="w-fit"
                        buttonStyle={ButtonStyles.Primary}
                        content={
                            <div className="w-[200px] rounded-lg flex flex-col">
                                <ListItem title={'Resources'} onClick={() => openResourcesDrawer()} />
                                <ListItem
                                    title={'Naming convention'}
                                    onClick={() => {
                                        openNameConventionDrawer();
                                    }}
                                />
                            </div>
                        }
                    />
                </div>
            </div>
            <div className="overflow-y-auto no-scrollbar h-[232px]">
                {tierPartRows.length ? (
                    <div className="p-2">{tierPartRows}</div>
                ) : (
                    <div className="h-full w-full flex flex-col justify-center items-center">
                        <EmptyState title="No resources added" description={emptyStateDescription} />
                    </div>
                )}
            </div>

            <ResourcesDrawer
                tierPart={tierPartConfig}
                updateTierPartScope={(newTierPart) => updateTierPart(scope, newTierPart)}
                defaultSelected={resourceToEdit}
                open={isResourcesDrawerOpen}
                scope={scope}
                onClose={() => {
                    setResourceEdit();
                    setIsResourcesDrawerOpen(false);
                }}
            />
            <NameConventionDrawer
                conventions={tierPartConfig.conditions}
                defaultSelectedConvention={conditionToEdit}
                open={isNameConventionDrawerOpen}
                onSaveConventions={onUpdateConditions}
                scope={scope}
                onClose={() => {
                    setIsNameConventionDrawerOpen(false);
                    setConditionToEdit();
                }}
            />
        </div>
    );
};

export default TierPart;
